import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { format } from 'date-fns';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TableHeader } from '../../components/Containers/ContainerTable';
import HeaderBottom from '../../components/super-admin/HeaderBottom';
import { PENDING_REQUESTS_CHANGED_EVENT } from '../../components/super-admin/NavBarAdmin';
import SnackBarContext from '../../context/snackbar';
import UserContext from '../../context/user';
import useContainerApiRoutes from '../../hooks/api/useContainerApiRoutes';
import useUtils from '../../hooks/useUtils';
import { Container } from '../../../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingTop: theme.spacing(3.25),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      paddingBottom: theme.spacing(5),
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.grey[100],
      height: '100%',
    },
    tableWrap: {
      backgroundColor: 'white',
      borderRadius: 24,
      padding: theme.spacing(2),
      boxShadow: '0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
    },
  }),
);

// Per-container decision, defaulting to accept until the admin toggles it.
type Decision = { accept: boolean; reason: string };

const PendingRequests = () => {
  const classes = useStyles();
  const { isLoggedIn } = useContext(UserContext);
  const { showSnack } = useContext(SnackBarContext);
  const { getContainerTypeName, getShippingLineName } = useUtils();
  const { getPendingContainers, submitReuseDecisions } = useContainerApiRoutes();

  const [containers, setContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  const load = useCallback(() => {
    setLoading(true);
    getPendingContainers()
      .then((response) => {
        const list = response || [];
        setContainers(list);
        // Every container starts as an accept; the admin toggles individual ones.
        setDecisions(Object.fromEntries(list.map((c) => [c._id, { accept: true, reason: '' }])));
        window.dispatchEvent(new Event(PENDING_REQUESTS_CHANGED_EVENT));
      })
      .finally(() => setLoading(false));
  }, [getPendingContainers]);

  useEffect(() => {
    if (isLoggedIn) load();
  }, [isLoggedIn, load]);

  const setDecision = (id: string, patch: Partial<Decision>) =>
    setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const submitGroup = async (group: Container[]) => {
    setSubmitting(true);
    try {
      await submitReuseDecisions(
        group.map((c) => ({
          id: c._id,
          accept: decisions[c._id]?.accept ?? true,
          reason: decisions[c._id]?.reason,
        }))
      );
      const declined = group.filter((c) => decisions[c._id]?.accept === false).length;
      showSnack(
        `${group.length - declined} accepted, ${declined} declined for ${group[0].shippingLineBookingRef || 'request'}.`,
        'success'
      );
      load();
    } finally {
      setSubmitting(false);
    }
  };

  // One entry per shipping line export booking: containers requested under the
  // same booking review together. Containers without a booking ref fall back
  // to grouping by requester.
  const groups = useMemo(() => {
    const byBookingRef = new Map<string, Container[]>();
    for (const container of containers) {
      const key = container.shippingLineBookingRef || `requester-${container.requested_by_user_id || 'unknown'}`;
      byBookingRef.set(key, [...(byBookingRef.get(key) || []), container]);
    }
    return Array.from(byBookingRef.entries());
  }, [containers]);
  // Groups are expanded by default; only explicit collapses are tracked, so
  // newly loaded groups always start open.
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const toggleGroup = (key: string) =>
    setCollapsedGroups((collapsed) =>
      collapsed.includes(key) ? collapsed.filter((k) => k !== key) : [...collapsed, key],
    );
  const allExpanded = groups.every(([key]) => !collapsedGroups.includes(key));
  const toggleAll = () => setCollapsedGroups(allExpanded ? groups.map(([key]) => key) : []);

  return (
    <>
      <HeaderBottom subtext="Pending Reuse Requests" showDateFilter={false} />
      <Box className={classes.container}>
        <Box className={classes.tableWrap} display="flex" flexDirection="column" flex={1} minHeight={0}>
          <Box display="flex" justifyContent="flex-end" px={1} pb={1}>
            <Button
              size="small"
              color="secondary"
              startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              onClick={toggleAll}
              disabled={groups.length === 0}
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Box>
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableHeader label="CONTAINER #" />
                  <TableHeader label="TYPE" />
                  <TableHeader label="SHIPPING LINE" />
                  <TableHeader label="REQUESTED BY" />
                  <TableHeader label="CLIENT / PICK UP" />
                  <TableHeader label="REQUESTED" />
                  <TableHeader sx={{ minWidth: 320 }} label="DECISION" />
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(([groupKey, group]) => {
                  const expanded = !collapsedGroups.includes(groupKey);
                  const requester = group[0].user_requested;
                  return [
                    <TableRow
                      key={groupKey}
                      hover
                      sx={{ '& > td': { backgroundColor: 'grey.50' } }}
                    >
                      <TableCell colSpan={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <IconButton size="small" onClick={() => toggleGroup(groupKey)}>
                            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                          <Typography variant="body2" fontWeight={600}>
                            {group[0].shippingLineBookingRef || 'No booking ref'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {requester?.fullName ||
                              requester?.companyInformation?.companyName ||
                              requester?.email ||
                              'Unknown requester'}{' '}
                            · {group.length} container{group.length === 1 ? '' : 's'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell colSpan={2} />
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          disabled={submitting}
                          onClick={() => submitGroup(group)}
                        >
                          Submit ({group.length})
                        </Button>
                      </TableCell>
                    </TableRow>,
                    ...(expanded ? group : []).map((container) => {
                      const decision = decisions[container._id] || { accept: true, reason: '' };
                      return (
                        <TableRow key={container._id}>
                          <TableCell>{container.containerCode || 'N/A'}</TableCell>
                          <TableCell>{getContainerTypeName(container)}</TableCell>
                          <TableCell>{getShippingLineName(container)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {container.user_requested?.fullName ||
                                container.user_requested?.companyInformation?.companyName ||
                                container.user_requested?.email ||
                                'N/A'}
                            </Typography>
                            {container.user_requested?.companyInformation?.companyName ? (
                              <Typography variant="caption" color="textSecondary">
                                {container.user_requested.companyInformation.companyName}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {container.client?.companyName || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {container.clientCache?.address || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {container.updated_at ? format(new Date(container.updated_at), 'd MMM yyyy') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={decision.accept ? 'accept' : 'decline'}
                                onChange={(_e, value) => {
                                  if (value) setDecision(container._id, { accept: value === 'accept' });
                                }}
                              >
                                <ToggleButton value="accept" color="success">
                                  Accept
                                </ToggleButton>
                                <ToggleButton value="decline" color="error">
                                  Decline
                                </ToggleButton>
                              </ToggleButtonGroup>
                              {!decision.accept ? (
                                <TextField
                                  size="small"
                                  variant="standard"
                                  placeholder="Reason (optional)"
                                  value={decision.reason}
                                  onChange={(e) => setDecision(container._id, { reason: e.target.value })}
                                />
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    }),
                  ];
                })}
                {!loading && containers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="textSecondary" align="center" py={4}>
                        No pending reuse requests.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default PendingRequests;
